import { Checkbox } from '@timelyapp/react';
import classNames from 'classnames';
import DiagnosticResourceId from 'components/dev-tools/DiagnosticResourceId';
import type { DropActionAreaSpecifier, MemoryDndItem } from 'constants/memory-dnd';
import MemoryDnD from 'constants/memory-dnd';
import React from 'react';
import { useDrag } from 'react-dnd';
import FontAwesome from 'react-fontawesome';
import { useDispatch } from 'react-redux';
import {
  toggleEntryIdsOnHourDraft,
  toggleEntryIdsOnPendingDraft,
} from 'redux/modules/timeline.memory.actions';
import type { ThunkDispatch } from 'types/redux';
import type { Memory } from 'types/timeline';
import { OperatorMode } from 'types/timeline';
import styles from './MemoryItem.module.scss';

interface Props {
  memory: Memory;
  hasEnabledTic?: boolean;
  toggleExpandEntryId?: (entry_id: number) => unknown;
  dragSource: DropActionAreaSpecifier;
  selectedGroupSection?: boolean;
  operatorMode: OperatorMode;
}

export const MemoryItem = (props: Props) => {
  const { memory, toggleExpandEntryId, dragSource } = props;
  const dispatch = useDispatch<ThunkDispatch>();

  const [_dragProps, dragRef] = useDrag(() => {
    const dragItem: MemoryDndItem = {
      memoryIds: [memory.id],
      source: dragSource,
    };
    return {
      type: MemoryDnD.TYPE,
      item: dragItem,
    };
  }, [memory, dragSource]);

  const hideCheckbox =
    props.operatorMode === OperatorMode.onLoggedHour ||
    props.operatorMode === OperatorMode.onSuggestion;

  const handleToggleMemoryId = (id: number | null) => {
    if (!id || hideCheckbox) return;

    if (
      props.operatorMode === OperatorMode.memoryInbox ||
      props.operatorMode === OperatorMode.pendingDraft
    ) {
      dispatch(toggleEntryIdsOnPendingDraft([id]));
      return;
    }

    dispatch(toggleEntryIdsOnHourDraft([id]));
  };

  const handleClickMemory = () => {
    if (hideCheckbox) {
      handleExpandMemory();
      return;
    }
    handleToggleMemoryId(memory.id);
  };

  const handleExpandMemory = () => {
    if (toggleExpandEntryId) {
      toggleExpandEntryId(memory.id);
    }
  };

  const hasSubMemories = memory.subEntries.length > 0;
  const showSubMemories = memory.isExpanded && hasSubMemories;

  const subEntryClass = classNames(styles.memory, {
    [styles.isChecked]: memory.isChecked,
    [styles.isLogged]: memory.isLogged,
    [styles.selectedGroupSection]: props.selectedGroupSection,
    [styles.clickingExpands]: hideCheckbox,
  });

  return (
    <div ref={dragRef} key={memory.id} data-memory-item data-testid="memory_item_container">
      <div className={subEntryClass}>
        {!hideCheckbox && (
          <Checkbox
            className={styles.checkbox}
            checked={!!memory.isChecked}
            onChange={handleClickMemory}
          />
        )}
        <div className={styles.content}>
          <span
            className={classNames(styles.title, 'fs-mask')}
            title={memory.description}
            onClick={handleClickMemory}
            data-testid="memory_item_title"
          >
            <DiagnosticResourceId resource="memory" id={memory.id} />
            {memory.title}
          </span>
          {memory.description && (
            <small
              className="fs-mask"
              title={memory.description}
              onClick={handleClickMemory}
              data-testid="memory_item_desc"
            >
              &nbsp;&bull; {memory.description}
            </small>
          )}

          {memory.project.name ? (
            <div
              title={`Logged to ${memory.project.name}`}
              className={styles.projectIndicator}
              style={{ background: memory.project.color || undefined }}
            />
          ) : undefined}
        </div>

        <div className={styles.link} data-testid="memory_item_link">
          {memory.url && (
            <a href={memory.url} target="_blank" rel="noopener noreferrer">
              <FontAwesome name="link" />
            </a>
          )}
        </div>

        <div
          className={classNames(styles.timestamp, {
            [styles.disabledClick]: !hasSubMemories,
          })}
          onClick={hasSubMemories ? handleExpandMemory : undefined}
        >
          {memory.duration.total_seconds === 0 ? (
            memory.fromTime
          ) : (
            <span>
              {memory.fromTime} - {memory.toTime}
              {/* <DurationFormat format="h[h] mm[min]" value={memory.duration.total_minutes} /> */}
            </span>
          )}
          {memory.subEntries.length > 0 && (
            <FontAwesome name={memory.isExpanded ? 'caret-down' : 'caret-right'} />
          )}
        </div>
      </div>

      {showSubMemories && (
        <div className={classNames('fs-mask', styles.subEntries)}>
          <ul>
            {memory.subEntries.length > 0 &&
              memory.subEntries.map(subentry => (
                <li key={subentry.id} className={styles.subEntryItem}>
                  <div className={styles.subEntryNote}>
                    <DiagnosticResourceId resource="sub memory" id={subentry.id} />
                    {subentry.note}
                  </div>

                  <div className={styles.subEntryTimestamp}>
                    {subentry.fromTime} - {subentry.toTime}
                  </div>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
};
